export interface AdminSession {
  authenticated: boolean;
  email?: string;
  permission?: 'read-only-admin';
}

export interface AdminCatalogue {
  permission: 'read-only-admin';
  values: Array<Array<string | number | boolean>>;
}

export const ADMIN_GATEWAY_URL = (import.meta.env.VITE_ADMIN_GATEWAY_URL ?? '').trim().replace(/\/$/, '');

export class AdminGatewayError extends Error {
  readonly status: number;
  constructor(message: string, status = 0) {
    super(message);
    this.name = 'AdminGatewayError';
    this.status = status;
  }
}

async function request<T>(path: string): Promise<T> {
  if (!ADMIN_GATEWAY_URL) throw new AdminGatewayError('Administrator access is not configured.');
  let response: Response;
  try {
    response = await fetch(`${ADMIN_GATEWAY_URL}${path}`, { credentials: 'include', cache: 'no-store' });
  } catch {
    throw new AdminGatewayError('Administrator gateway is unreachable.');
  }
  const body = (await response.json().catch(() => null)) as T | { error?: string } | null;
  if (!response.ok) throw new AdminGatewayError(body && typeof body === 'object' && 'error' in body && body.error === 'unauthorized' ? 'Administrator authentication is required.' : 'Administrator gateway is unavailable.', response.status);
  return body as T;
}

export function getAdminSession(): Promise<AdminSession> {
  return request<AdminSession>('/session');
}

export function getAdminCatalogue(): Promise<AdminCatalogue> {
  return request<AdminCatalogue>('/catalogue');
}

export function openAdminSignIn(): void {
  if (!ADMIN_GATEWAY_URL) return;
  window.open(`${ADMIN_GATEWAY_URL}/session`, '_blank', 'noopener,noreferrer');
}

export function openAdminSignOut(): void {
  if (!ADMIN_GATEWAY_URL) return;
  window.open(`${ADMIN_GATEWAY_URL}/logout`, '_blank', 'noopener,noreferrer');
}
